/**
 * Chatbot Utility Functions
 */

import {
  staticChatbotData,
  generateServiceListResponse,
  generatePromosResponse,
  generateBranchesResponse,
  generateFeaturesResponse,
  generateFAQResponse,
  generateNavigationResponse,
  getStoreHoursBlock,
  getStoreHoursWithNaturalSentence,
} from './chatbotData';

/**
 * Main response handler
 */
export const findResponse = async (userInput) => {
  const input = userInput.toLowerCase().trim();

  // Greeting
  if (matchKeywords(input, staticChatbotData.responses.greeting.keywords)) {
    return staticChatbotData.responses.greeting.response;
  }

  // Help
  if (matchKeywords(input, staticChatbotData.responses.help.keywords)) {
    return staticChatbotData.responses.help.response;
  }

  // Services
  if (
    matchKeywords(input, [
      'services', 'service', 'treatments', 'treatment', 'what do you offer', 'what services',
    ])
  ) {
    return await generateServiceListResponse();
  }

  // Specific services
  if (
    matchKeywords(input, [
      'massage', 'facial', 'aromatherapy', 'yoga', 'counseling', 'spa',
    ])
  ) {
    return await generateServiceListResponse();
  }

  // Features
  if (
    matchKeywords(input, [
      'features', 'feature', 'what can i do', 'capabilities', 'hygea can do',
    ])
  ) {
    return await generateFeaturesResponse();
  }

  // Booking
  if (
    matchKeywords(input, [
      'book', 'booking', 'appointment', 'schedule', 'reserve',
    ])
  ) {
    return `To book an appointment at Hygea:

1. Go to the "Treatments" section
2. Select a service
3. Choose date and time
4. Complete booking
5. Receive confirmation

${getStoreHoursWithNaturalSentence()}

Need help with anything else?`;
  }

  // Availability
  if (
    matchKeywords(input, [
      'availability', 'available', 'available slots', 'slots', 'slot', 'time available',
    ])
  ) {
    return `I'd be happy to help with availability.

${getStoreHoursBlock()}

You can check available slots in the "Treatments" section after selecting your preferred service and date.`;
  }

  // Cancel / Reschedule
  if (
    matchKeywords(input, [
      'cancel', 'reschedule', 'change appointment', 'modify booking',
    ])
  ) {
    return `You can cancel or reschedule up to 24 hours before:

1. Go to "History"
2. Select appointment
3. Click Cancel/Reschedule
4. Confirm

Note: Late cancellations may incur fees.`;
  }

  // Pricing
  if (
    matchKeywords(input, [
      'price', 'cost', 'pricing', 'how much', 'fee',
    ])
  ) {
    return await generateServiceListResponse();
  }

  // FAQ
  if (
    matchKeywords(input, [
      'faq', 'questions', 'policy', 'payment', 'discount',
    ])
  ) {
    return generateFAQResponse(input);
  }

  // Location
  if (
    matchKeywords(input, [
      'location', 'branch', 'address', 'near me',
    ])
  ) {
    return await generateBranchesResponse();
  }

  // Navigation
  if (
    matchKeywords(input, [
      'navigate', 'how to use', 'guide',
    ])
  ) {
    return generateNavigationResponse();
  }

  // Account
  if (
    matchKeywords(input, [
      'login', 'password', 'account', 'forgot',
    ])
  ) {
    return `Forgot Password:
1. Click "Forgot Password"
2. Enter email
3. Check email for reset link

Still having issues? Contact support or request a live agent.`;
  }

  // Contact
  if (
    matchKeywords(input, [
      'contact', 'email', 'phone', 'support',
    ])
  ) {
    return `Support options:
- In-app chat
- Email
- Phone
- Type "I want to talk to a real agent"

We're here to help!`;
  }

  // History
  if (
    matchKeywords(input, [
      'history', 'receipt', 'past', 'booking history',
    ])
  ) {
    return `View your history:
1. Go to "History"
2. Select appointment
3. View/download receipt`;
  }

  // Notifications
  if (
    matchKeywords(input, [
      'notification', 'reminder', 'alert',
    ])
  ) {
    return `You'll receive:
- Booking confirmations
- Appointment reminders
- Updates and promos

Customize in Settings.`;
  }

  // Promos
  if (
    matchKeywords(input, [
      'promo', 'discount', 'offer', 'deal',
    ])
  ) {
    return await generatePromosResponse();
  }

  // Profile
  if (
    matchKeywords(input, [
      'profile', 'settings', 'preferences',
    ])
  ) {
    return `Manage your account in Profile and Settings:
- Edit personal info
- Manage notifications
- Update password`;
  }

  // Staff
  if (
    matchKeywords(input, [
      'therapist', 'staff', 'professional',
    ])
  ) {
    return `Hygea professionals include:
- Massage therapists
- Estheticians
- Yoga instructors
- Counselors

All certified and experienced.`;
  }

  // Working Hours
  if (
    matchKeywords(input, [
      'hours', 'working hours', 'open', 'closed', 'timing', 'when are you open', 'what time',
    ])
  ) {
    return getStoreHoursWithNaturalSentence();
  }

  // Escalation trigger
  if (input.includes('real agent')) {
    return 'Your request has been forwarded to live support. Please wait for an agent.';
  }

  // Default fallback
  return staticChatbotData.responses.defaultFallback;
};

/**
 * Keyword matcher
 */
const matchKeywords = (input, keywords) => {
  return keywords.some((keyword) => input.includes(keyword.toLowerCase()));
};

/**
 * Extract keywords (for future AI/NLP)
 */
export const extractKeyPhrases = (input) => {
  const words = input.toLowerCase().split(/\s+/);
  return words.filter(
    (word) =>
      word.length > 3
      && !['what', 'when', 'where', 'which', 'that', 'this'].includes(word)
  );
};
